import React, { useState, useEffect, useRef } from 'react';
import { User, Hotel } from '../types';
import { apiService } from '../services/api';
import { Button, Input, Select, Card, CardContent, ConfirmDialog } from './ui';
import { Edit3, Trash2, UserPlus, Users, Shield, Building2, Loader2, Eye, Search } from 'lucide-react';
import { useUser } from '../contexts/UserContext';

interface UserManagementProps {
  className?: string;
}

export function UserManagement({ className = '' }: UserManagementProps) {
  const { startMimicking, isMimicking } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Refs for timeout cleanup
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper functions for timeout management
  const setSuccessTimeout = (callback: () => void, delay: number = 5000) => {
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(callback, delay);
  };

  const setErrorTimeout = (callback: () => void, delay: number = 5000) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(callback, delay);
  };
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [usersData, hotelsData] = await Promise.all([
        apiService.getUsers(),
        apiService.getHotels()
      ]);
      setUsers(usersData);
      setHotels(hotelsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load users and hotels');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (userData: {
    email: string;
    role: 'admin' | 'management' | 'hotel_staff';
    assignedHotelIds: string[];
  }) => {
    try {
      const newUser = await apiService.createUser({
        ...userData,
        displayName: userData.email.split('@')[0], // Temporary display name until Google sign-in
        isActive: true,
      });
      
      setUsers(prev => [...prev, newUser]);
      setShowAddUser(false);
      setShowSuccessMessage('User added successfully! They will need to sign in with Google to complete their profile setup.');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error adding user:', err);
      setError('Failed to add user. Please try again.');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleUpdateUser = async (email: string, updates: Partial<User>) => {
    try {
      const updatedUser = await apiService.updateUser(email, updates);
      setUsers(prev => prev.map(user => user.email === email ? updatedUser : user));
      setEditingUser(null);
      setShowSuccessMessage('User updated successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleDeleteUser = (email: string) => {
    const user = users.find(u => u.email === email);
    if (!user) return;
    
    setDeletingUser(user);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      await apiService.deleteUser(deletingUser.email);
      setUsers(prev => prev.filter(user => user.email !== deletingUser.email));
      setDeletingUser(null);
      setShowSuccessMessage('User deleted successfully!');
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error deleting user:', err);
      setDeletingUser(null);
      setError('Failed to delete user. Please try again.');
      setErrorTimeout(() => setError(null));
    }
  };

  const handleToggleUserStatus = async (email: string, isActive: boolean) => {
    try {
      if (isActive) {
        await apiService.activateUser(email);
      } else {
        await apiService.deactivateUser(email);
      }
      
      setUsers(prev => prev.map(user => 
        user.email === email ? { ...user, isActive } : user
      ));
      
      setShowSuccessMessage(`User ${isActive ? 'activated' : 'deactivated'} successfully!`);
      setSuccessTimeout(() => setShowSuccessMessage(null));
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Failed to update user status. Please try again.');
      setErrorTimeout(() => setError(null));
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Search by name
    if (user.displayName?.toLowerCase().includes(query)) return true;
    
    // Search by email
    if (user.email.toLowerCase().includes(query)) return true;
    
    // Search by role
    const roleMapping: { [key: string]: string[] } = {
      'admin': ['admin', 'administrator'],
      'management': ['management', 'manager'],
      'hotel_staff': ['hotel staff', 'hotel_staff', 'staff', 'hotel']
    };
    
    const roleSearchTerms = roleMapping[user.role] || [];
    if (roleSearchTerms.some(term => term.includes(query) || query.includes(term))) {
      return true;
    }
    
    // Search by hotel name
    if (user.assignedHotelIds && user.assignedHotelIds.length > 0) {
      const userHotels = hotels.filter(hotel => user.assignedHotelIds.includes(hotel.id));
      return userHotels.some(hotel => hotel.name.toLowerCase().includes(query));
    }
    
    return false;
  });

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 ${className}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900">User Management</h2>
            <p className="text-gray-600 font-text">Manage system users and their access</p>
          </div>
        </div>
        
        <Button
          onClick={() => setShowAddUser(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-2xl">
          <p className="text-green-700">{showSuccessMessage}</p>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          type="text"
          placeholder="Search by name, email, role, or hotel name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-11 pr-4 py-3 w-full bg-white/60 backdrop-blur-xl border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary-400 focus:border-transparent"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
          >
            <span className="text-lg">×</span>
          </button>
        )}
      </div>

      {/* Results count */}
      {searchQuery && (
        <div className="text-sm text-gray-600">
          Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </div>
      )}

      {/* Users List */}
      <div className="grid gap-4">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">
              {searchQuery 
                ? `No users match your search "${searchQuery}"`
                : 'No users have been added yet'
              }
            </p>
          </div>
        ) : (
          filteredUsers.map((user) => (
          <Card key={user.email} className="bg-white/40 backdrop-blur-xl border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className="w-12 h-12 rounded-2xl object-cover border border-gray-200"
                    />
                  ) : (
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-6 h-6" />
                      ) : (
                        <Building2 className="w-6 h-6" />
                      )}
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-display font-bold text-gray-900">
                      {user.displayName}
                    </h3>
                    <p className="text-gray-600 font-text">{user.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-700' 
                          : user.role === 'management'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role === 'admin' ? 'Admin' : user.role === 'management' ? 'Management' : 'Hotel Staff'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.isActive 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startMimicking(user)}
                    className="text-blue-600 hover:text-blue-700"
                    disabled={isMimicking}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingUser(user)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleUserStatus(user.email, !user.isActive)}
                    className={user.isActive 
                      ? 'text-orange-600 hover:text-orange-700' 
                      : 'text-green-600 hover:text-green-700'
                    }
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteUser(user.email)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {user.role === 'hotel_staff' && user.assignedHotelIds.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Assigned Hotels:</p>
                  <div className="flex flex-wrap gap-2">
                    {user.assignedHotelIds.map(hotelId => {
                      const hotel = hotels.find(h => h.id === hotelId);
                      return (
                        <span
                          key={hotelId}
                          className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                        >
                          {hotel?.name || 'Unknown Hotel'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <AddUserForm
          hotels={hotels}
          onSave={handleAddUser}
          onCancel={() => setShowAddUser(false)}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserForm
          user={editingUser}
          hotels={hotels}
          onSave={(updates) => handleUpdateUser(editingUser.email, updates)}
          onCancel={() => setEditingUser(null)}
        />
      )}

      {/* Confirm Delete User Dialog */}
      <ConfirmDialog
        isOpen={deletingUser !== null}
        onClose={() => setDeletingUser(null)}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={deletingUser ? `Are you sure you want to delete user "${deletingUser.email}"? This action cannot be undone.` : ''}
        variant="danger"
        confirmText="Delete User"
      />
    </div>
  );
}

// Add User Form Component
interface AddUserFormProps {
  hotels: Hotel[];
  onSave: (userData: {
    email: string;
    role: 'admin' | 'management' | 'hotel_staff';
    assignedHotelIds: string[];
  }) => void;
  onCancel: () => void;
}

function AddUserForm({ hotels, onSave, onCancel }: AddUserFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    role: 'hotel_staff' as 'admin' | 'management' | 'hotel_staff',
    assignedHotelIds: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-display font-bold text-gray-900">Add User</h2>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            ×
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="user@company.com"
              required
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Display name and profile picture will be automatically retrieved from Google when the user first signs in.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <Select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                role: e.target.value as 'admin' | 'management' | 'hotel_staff',
                assignedHotelIds: e.target.value === 'admin' || e.target.value === 'management' ? [] : prev.assignedHotelIds
              }))}
              options={[
                { value: 'hotel_staff', label: 'Hotel Staff' },
                { value: 'management', label: 'Management' },
                { value: 'admin', label: 'Admin' }
              ]}
              className="w-full"
            />
          </div>

          {formData.role === 'hotel_staff' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Hotels
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {hotels.map((hotel) => (
                  <label key={hotel.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.assignedHotelIds.includes(hotel.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            assignedHotelIds: [...prev.assignedHotelIds, hotel.id]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            assignedHotelIds: prev.assignedHotelIds.filter(id => id !== hotel.id)
                          }));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{hotel.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-4 pt-6">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Add User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit User Form Component
interface EditUserFormProps {
  user: User;
  hotels: Hotel[];
  onSave: (updates: Partial<User>) => void;
  onCancel: () => void;
}

function EditUserForm({ user, hotels, onSave, onCancel }: EditUserFormProps) {
  const [formData, setFormData] = useState({
    displayName: user.displayName,
    role: user.role,
    assignedHotelIds: user.assignedHotelIds,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-display font-bold text-gray-900">Edit User</h2>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            ×
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={user.email}
              disabled
              className="w-full bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <Input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="John Doe"
              required
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <Select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                role: e.target.value as 'admin' | 'management' | 'hotel_staff',
                assignedHotelIds: e.target.value === 'admin' || e.target.value === 'management' ? [] : prev.assignedHotelIds
              }))}
              options={[
                { value: 'hotel_staff', label: 'Hotel Staff' },
                { value: 'management', label: 'Management' },
                { value: 'admin', label: 'Admin' }
              ]}
              className="w-full"
            />
          </div>

          {formData.role === 'hotel_staff' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Hotels
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {hotels.map((hotel) => (
                  <label key={hotel.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.assignedHotelIds.includes(hotel.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            assignedHotelIds: [...prev.assignedHotelIds, hotel.id]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            assignedHotelIds: prev.assignedHotelIds.filter(id => id !== hotel.id)
                          }));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{hotel.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end space-x-4 pt-6">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
